import { MeshFlags } from '../../../scene/MeshFlags.js';
import { LmvMatrix4 } from '../../../scene/LmvMatrix4.js';
import { $wgsl } from "../../../wgsl-preprocessor/wgsl-preprocessor";
import objectUniforms from "../object_uniforms.wgsl";

/**
 * @param {number} bindGroup
 * @param {boolean} hasInstanceIdMap
 * @returns {string}
 */
export function getObjectUniformsDeclaration(bindGroup, hasInstanceIdMap) {
  return $wgsl(objectUniforms, {
    bindGroup,
    hasInstanceIdMap,
    // Typical rendering needs material bindings as well.
    includeNonObjectBindings: true
  });
}

/**
 * @param {number} bindGroup
 * @returns {string}
 */
export function getOnlyObjectUniformsDeclaration(bindGroup) {
  return $wgsl(objectUniforms, {
    bindGroup,
    hasInstanceIdMap: false,
    // Only the object uniform binding is needed.
    includeNonObjectBindings: false
  });
}

// Memory layout of the ObjectUniforms for a single fragment in the batched GPU UniformBuffer
// All offsets are defined in 32-bit Items (float/int)
export const UniformOffsets = {
  transform: 0, // 12 floats
  dbId: 12, // All remaining values are Int32
  modelId: 13,
  flags: 14,
  themingColor: 15,
  materialReference: 16,
  meshAnimTime: 17,
  lineWidthOrPointSize: 18, // line width for wide lines, or point size for point geometry (mutually exclusive)
  highlightingCategory: 19,
  SIZE_IN_FLOATS: 20
};

// Stride size of a single entry in the ObjectUniforms buffer
export const OBJECT_STRIDE_32 = UniformOffsets.SIZE_IN_FLOATS;
export const OBJECT_STRIDE = UniformOffsets.SIZE_IN_FLOATS * 4; // in bytes

// The flags that we need per-fragment on GPU is not identical with the full set of flags
// in the fragmentList. Therefore, we use an own flag type to identify flags as relevant for GPU.
export const FragmentFlagsGPU = {
  VISIBLE: 1,
  HIGHLIGHTED: 2 // only used for the simpler "regular" highlight that just colors in blue.
};

// This class defines the way how per-fragment uniforms are batched into a single float32 UniformBuffers.
// It is used by ObjectUniformBuilder to create and GPU-upload ranges of Uniforms for multiple fragments.
export class ObjectUniforms {

  // Different views to the CPU-side buffer
  /** @type Float32Array */
  #data;
  /** @type Int32Array */
  #dataInt;
  /** @type Uint8Array */
  #dataUint8;

  #tmpMtx = new LmvMatrix4(false);

  /**
   * @param {number} capacity - maximum number of items
   */
  constructor(capacity) {
    // CPU-side buffer for temporary storage before upload
    this.#data = new Float32Array(OBJECT_STRIDE_32 * capacity);
    this.#dataInt = new Int32Array(this.#data.buffer);
    this.#dataUint8 = new Uint8Array(this.#data.buffer);
  }

  // Buffer size in items
  length() {
    return this.#data.length / OBJECT_STRIDE_32;
  }

  // Copy fragment transform data to the CPU buffer.
  #setTransformFromFragment(
  objectIndex, // which object to write to. Must be in range [0, length)
  fragmentList,
  fragId)
  {
    // If animations or model transform is involved, we cannot directly
    // read the raw transforms.
    const moved = fragmentList.vizflags[fragId] & MeshFlags.MESH_MOVED;
    const hasModelTf = Boolean(fragmentList.matrix);
    const transforms = fragmentList.transforms;
    if (moved || hasModelTf || !transforms) {
      fragmentList.getWorldMatrix(fragId, this.#tmpMtx);
      this.setTransform(objectIndex, this.#tmpMtx);
      return;
    }

    // Faster variant: In most cases, we can read raw transform values

    const TRANSFORM_STRIDE = 12;
    const readOffset = fragId * TRANSFORM_STRIDE;
    const writeOffset = objectIndex * OBJECT_STRIDE_32; // write start position in floats

    for (let i = 0; i < 12; i++) {
      this.#data[writeOffset + i] = transforms[readOffset + i];
    }
  }

  // Set from LmvMatrix4 (must be float32)
  setTransform(objectIndex, matrix) {
    const writeOffset = objectIndex * OBJECT_STRIDE_32;

    this.#data[writeOffset] = matrix.elements[0];
    this.#data[writeOffset + 1] = matrix.elements[1];
    this.#data[writeOffset + 2] = matrix.elements[2];
    this.#data[writeOffset + 3] = matrix.elements[4];
    this.#data[writeOffset + 4] = matrix.elements[5];
    this.#data[writeOffset + 5] = matrix.elements[6];
    this.#data[writeOffset + 6] = matrix.elements[8];
    this.#data[writeOffset + 7] = matrix.elements[9];
    this.#data[writeOffset + 8] = matrix.elements[10];
    this.#data[writeOffset + 9] = matrix.elements[12];
    this.#data[writeOffset + 10] = matrix.elements[13];
    this.#data[writeOffset + 11] = matrix.elements[14];
  }

  /**
   * Set all object uniforms except transform.
   * @param {number} objectIndex array index of the object in the GPUBuffer.
   * @param {number} dbId
   * @param {number} modelId
   * @param {number} fragmentFlagsGPU BitMask of FragmentFlagsGPU values
   * @param {number} themingColor Theming color as ABGR integer.
   * @param {number} materialReference Material reference index.
   * @param {number} meshAnimTime Loading animation percent for 2d meshes.
   * @param {number} lineWidthOrPointSize Line width for wide lines, or point size for point geometry (0 for regular geometry).
   * @param {number} highlightingCategory Highlighting category (used to form object categories for category highlighting per object).
   */
  #setOtherUniforms(objectIndex, dbId, modelId, fragmentFlagsGPU, themingColor, materialReference, meshAnimTime, lineWidthOrPointSize = 0, highlightingCategory = 0) {
    const writeOffset = objectIndex * OBJECT_STRIDE_32;
    this.#dataInt[writeOffset + UniformOffsets.dbId] = dbId;
    this.#dataInt[writeOffset + UniformOffsets.modelId] = modelId;
    this.#dataInt[writeOffset + UniformOffsets.flags] = fragmentFlagsGPU;
    this.#dataInt[writeOffset + UniformOffsets.themingColor] = themingColor;
    this.#dataInt[writeOffset + UniformOffsets.materialReference] = materialReference;
    this.#data[writeOffset + UniformOffsets.meshAnimTime] = meshAnimTime;
    this.#data[writeOffset + UniformOffsets.lineWidthOrPointSize] = lineWidthOrPointSize;
    this.#dataInt[writeOffset + UniformOffsets.highlightingCategory] = highlightingCategory;
  }

  // Wide-line width and point size are mutually exclusive and share the same uniform slot.
  // Returns the relevant size for the geometry type, or 0 for regular geometry.
  #getLineWidthOrPointSize(geometry, material) {
    if (!geometry || !material) {
      return 0;
    }
    if (geometry.isPoints) {
      // Note geometry.pointSize isn't used here as WebGL always uses material.size and
      // MaterialManager.setupMaterial() writes the geometry's .pointSize to material.size anyway.
      return material.size ?? 1.0;
    }
    if (material.wideLines) {
      // Wide-line width. Material value has precedence (for consistency with the WebGLRenderer implementation).
      return material.linewidth ?? geometry.lineWidth;
    }
    return 0;
  }

  // write object uniform entry from single fragment
  setFromFragment(objectIndex, fragmentList, fragId, materialReference) {

    const dbId = fragmentList.fragments.fragId2dbId[fragId];
    const modelId = fragmentList.modelId;
    const fragmentFlagsGPU = readFragmentFlagsGPU(fragmentList, fragId);
    const themingColor = readThemingColor(fragmentList, fragId);
    const meshAnimTime = 1; // Only varies for 2d meshes.

    const highlightingCategory = fragmentList.highlightingCategoryCB?.(dbId) ?? 0;

    const material = fragmentList.getMaterial(fragId);
    const geometry = fragmentList.getGeometry(fragId);
    const lineWidthOrPointSize = this.#getLineWidthOrPointSize(geometry, material);

    this.#setTransformFromFragment(objectIndex, fragmentList, fragId);
    this.#setOtherUniforms(objectIndex, dbId, modelId, fragmentFlagsGPU, themingColor, materialReference, meshAnimTime, lineWidthOrPointSize, highlightingCategory);
  }

  // Same as setFromFragment, but obtaining the values from a mesh object.
  // Note that only transform is a standard prop - all others usually only exist for meshes
  // obtained via getVizMesh() for 2D models.
  setFromObject(mesh, objectIndex, materialReference) {

    this.setTransform(objectIndex, mesh.matrixWorld);

    // get theming color (may be undefined or ignored if intensity value w is 0)
    const themingColor = mesh.themingColor;
    let themingColorGPU = 0;
    if (themingColor && themingColor.w > 0.0) {
      themingColorGPU = vectorToABGR(mesh.themingColor);
    }
    const meshAnimTime = mesh.meshAnimTime ?? 1;

    const lineWidthOrPointSize = this.#getLineWidthOrPointSize(mesh.geometry, mesh.material);

    this.#setOtherUniforms(
      objectIndex,
      mesh.dbId,
      mesh.modelId,
      0, // currently, we use flags only for FragmentList entries - 0 otherwise
      themingColorGPU,
      materialReference,
      meshAnimTime,
      lineWidthOrPointSize,
      mesh.highlightingCategory ?? 0 // tandem-specific property: Usually only available for
    );
  }

  /*
   * Set the object data for a range of objects by getting the data from an instanced mesh.
   */
  setObjectDataFromInstanceBuffer(geometry, itemOffset, materialReference) {
    const attrib = geometry.attributes.instanceUniforms;
    const srcData = attrib.array;
    const dstData = this.#dataUint8;
    const byteOffset = itemOffset * OBJECT_STRIDE;
    dstData.set(srcData, byteOffset);

    // fill in (identical) material reference to all instances
    for (let i = 0; i < geometry.numInstances; i++) {
      const offset = (itemOffset + i) * OBJECT_STRIDE_32;
      this.setMaterialReference(offset, materialReference);
    }
  }

  // Write only material reference for a single object to CPU-side buffer.
  // Note: Having to use the int32 offset instead of ObjectIndex is just for backwards compatibility
  //       with the old ObjectUniforms. This should later be simplified by another function that just works with objectIndex directly.
  setMaterialReference(
  writeOffset, // int32 offset
  materialReference)
  {
    this.#dataInt[writeOffset + UniformOffsets.materialReference] = materialReference;
  }

  // Upload a single int-value
  uploadIntValue(
  device,
  dstBuffer,
  dstObjectIndex, // index of the item in the buffer for which this uniform changed
  attribOffset, // offset in 32-Bit ints within the item. Determines which uniform is updated, e.g. UniformOffsets.dbId
  value)
  {
    this.#dataInt[0] = value;

    const writeOffsetGPU = dstObjectIndex * OBJECT_STRIDE + 4 * attribOffset; // in bytes
    device.queue.writeBuffer(dstBuffer, writeOffsetGPU, this.#data.buffer, 0, 4);
  }

  // Upload just a range of bytes (usually a single uniform value, e.g. transform, theming color etc) of the uniform data.
  uploadTransform(
  device,
  dstBuffer,
  dstObjectIndex, // index of the item in the buffer for which this uniform changed
  fragList,
  fragId)
  {
    // Write fragment transform to the CPU buffer
    this.#setTransformFromFragment(0, fragList, fragId);

    // get offsets in bytes
    const attribOffset = 4 * UniformOffsets.transform;
    const readOffsetCPU = attribOffset;
    const writeOffsetGPU = dstObjectIndex * OBJECT_STRIDE + attribOffset;

    // Upload transform
    const byteLength = 48; // 12 floats * 4 bytes
    device.queue.writeBuffer(dstBuffer, writeOffsetGPU, this.#data.buffer, readOffsetCPU, byteLength);
  }

  /**
   * Sets and uploads the theming color uniform for the object at the specified index.
   * @param {GPUDevice} device
   * @param {GPUBuffer} dstBuffer
   * @param {number} dstObjectIndex The index within the GPU buffer of the object to update.
   * @param {THREE.Vector4} color The theming color vector of the fragment.
   */
  uploadThemingColor(device, dstBuffer, dstObjectIndex, color) {

    const value = color.w > 0.0 ? vectorToABGR(color) : 0;
    this.#dataInt[UniformOffsets.themingColor] = value;

    // get offsets in bytes
    const attribOffset = 4 * UniformOffsets.themingColor;
    const readOffsetCPU = attribOffset;
    const writeOffsetGPU = dstObjectIndex * OBJECT_STRIDE + attribOffset;

    // Upload theming color
    const byteLength = 4;
    device.queue.writeBuffer(dstBuffer, writeOffsetGPU, this.#data.buffer, readOffsetCPU, byteLength);
  }

  // Upload this buffer (or a range of it) to a GPU buffer.
  upload(device, dstBuffer, count = this.length(), dstStart = 0, srcStart = 0, alignTo256 = false) {
    const byteOffset = dstStart * OBJECT_STRIDE;
    let byteLength = count * OBJECT_STRIDE;

    // TODO: This was kept from original code, but it seems unnecessary.
    if (alignTo256) {
      //round up to next multiple of 256
      byteLength = byteLength + 255 & 0xffffff00;

      // We need to clamp the rounded-up write size to not go past the end of the source buffer.
      const maxByteLength = this.#data.buffer.byteLength - srcStart * OBJECT_STRIDE;
      if (byteLength > maxByteLength) {
        byteLength = maxByteLength;
      }
    }

    device.queue.writeBuffer(dstBuffer, byteOffset, this.#data.buffer, srcStart * OBJECT_STRIDE, byteLength);
  }
}

// Convert fragment vizFlags to GPU flags
function toFragmentFlagsGPU(vizFlags) {
  // TODO: Should this just pass all vizFlags along?
  let gpuFlags =
  (vizFlags & MeshFlags.MESH_VISIBLE ? FragmentFlagsGPU.VISIBLE : 0) | (
  vizFlags & MeshFlags.MESH_HIGHLIGHTED ? FragmentFlagsGPU.HIGHLIGHTED : 0);

  return gpuFlags;
}

// Read fragment theming color as ABGR integer (0 if not themed)
function readThemingColor(fragList, fragId) {
  const themingColor = fragList.getThemingColor(fragId);
  const isThemed = themingColor && themingColor.w > 0.0;
  return isThemed ? vectorToABGR(themingColor) : 0;
}

// Obtain shader flags for a single fragment.
export function readFragmentFlagsGPU(fragList, fragId) {
  const vizFlags = fragList.vizflags[fragId];
  const gpuFlags = toFragmentFlagsGPU(vizFlags);
  return gpuFlags;
}

export function vectorToABGR(v) {
  return v.x * 255 | v.y * 255 << 8 | v.z * 255 << 16 | v.w * 255 << 24;
}

export function colorToABGR(c, alpha = 1.0) {
  return c.r * 255 | c.g * 255 << 8 | c.b * 255 << 16 | alpha * 255 << 24;
}